/**
 * The member detail pane lives at `?view=member&memberId=` on the existing
 * `/pools` route, not under a route file of its own (ISSUE-194).
 *
 * Two independent reasons, either of which is enough:
 *
 *  1. `apps/app/src/routes/__root.tsx` holds the only `<Outlet />` in the
 *     routes tree, so a `pools.member.tsx` child route would never paint.
 *     This is the same constraint `poolMembersRoute.ts` records.
 *  2. `infra/ci/checkRouteCoverage.ts` requires every route file to be reached
 *     by a literal `page.goto()` in a browser spec. `/pools` is already
 *     credited; a pane needs no new spec.
 *
 * Every screen and every navigation reads and writes this URL state through
 * the four functions below, never through `search.view` or `search.memberId`
 * directly.
 */
import type { POOLS_VIEWS } from "../../routeState/sectionSearch";
import { POOL_MEMBERS_TAB } from "../poolMembers/poolMembersRoute";
import type { typePoolTab } from "../poolScreen/poolScreenRoute";

export const POOL_MEMBER_DETAIL_VIEW: (typeof POOLS_VIEWS)[number] = "member";

type typeDetailSearch = {
  poolId?: string | undefined;
  view?: string | undefined;
  memberId?: string | undefined;
};

/**
 * A member id is required, not just a view. Without it there is nobody to
 * show, so `pools.tsx` falls through to the pool home rather than painting an
 * empty pane at a URL that looks like it should have content.
 */
export function isPoolMemberDetailOpen(search: typeDetailSearch): boolean {
  return (
    search.view === POOL_MEMBER_DETAIL_VIEW &&
    typeof search.poolId === "string" &&
    search.poolId !== "" &&
    typeof search.memberId === "string" &&
    search.memberId !== ""
  );
}

export function readPoolMemberDetailTarget(
  search: typeDetailSearch,
): string | null {
  if (search.view !== POOL_MEMBER_DETAIL_VIEW) return null;
  if (typeof search.memberId !== "string" || search.memberId === "") {
    return null;
  }
  return search.memberId;
}

/**
 * `sheet` is cleared on the way in. Opening a profile from a row while the
 * overflow dialog is open must not leave a stale sheet name in the URL.
 */
export function openPoolMemberDetailSearch<
  TSearch extends Record<string, unknown>,
>(
  search: TSearch,
  memberId: string,
): Omit<TSearch, "view" | "memberId" | "sheet"> & {
  view: typeof POOL_MEMBER_DETAIL_VIEW;
  memberId: string;
  sheet: undefined;
} {
  return {
    ...search,
    view: POOL_MEMBER_DETAIL_VIEW,
    memberId,
    sheet: undefined,
  };
}

/** Back to the roster this profile was opened from, carrying no member with it. */
export function closePoolMemberDetailSearch<
  TSearch extends Record<string, unknown>,
>(
  search: TSearch,
): Omit<TSearch, "view" | "memberId" | "sheet" | "tab"> & {
  tab: typePoolTab;
} {
  const dropped = new Set<string>(["view", "sheet", "memberId", "tab"]);
  const kept = Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as Omit<TSearch, "view" | "memberId" | "sheet" | "tab">;
  return { ...kept, tab: POOL_MEMBERS_TAB };
}
